import React from "react";

import PeopleIcon from "@uui/Users03";
import { every } from "lodash/fp";

import Typography from "@mui/material/Typography";

import IntegrationProviderIcon from "@app/analytics/components/IntegrationProviderIcon";
import { INTEGRATION_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import { TOOLTIPS } from "@app/common/content";
import type { IntegrationAccessSetting } from "@app/common/types";
import { pluralizePhrase } from "@app/common/utils";
import type { EnterpriseWorkspaceSettings } from "@app/common/workspaces/types";

import { Column, Row } from "@rui/Stack";
import Switch from "@rui/Switch";
import TooltipWrapper from "@rui/TooltipWrapper";

import {
  INTEGRATION_DETAIL_BY_PROVIDER_ID,
  IntegrationSettingCategorySetting,
} from "./constants";
import { MemberCountChip, SettingRow } from "./styles";

const IntegrationCategoryRow = ({
  disabled,
  numConnected,
  onToggle,
  setting,
  settings,
}: {
  disabled: boolean;
  numConnected: number;
  onToggle: (opts: {
    settings: IntegrationAccessSetting[];
    enable: boolean;
  }) => void;
  setting: IntegrationSettingCategorySetting;
  settings: EnterpriseWorkspaceSettings;
}) => {
  const providerConfig = INTEGRATION_PROVIDERS_BY_ID[setting.provider];
  const integrationIsEnabled = every(
    (s) => settings[s] ?? false,
    setting.settings
  );

  const handleToggle = (_e: React.ChangeEvent, checked: boolean) => {
    onToggle({ settings: setting.settings, enable: checked });
  };

  return (
    <SettingRow spacing={1}>
      <Row spacing={2} variant="left">
        <TooltipWrapper
          show={disabled}
          title={TOOLTIPS.upsell.upgradeToPro}
          wrapperSx={{ height: "fit-content" }}
        >
          <Switch
            disabled={disabled}
            checked={integrationIsEnabled}
            onChange={handleToggle}
          />
        </TooltipWrapper>
        <Row spacing={2}>
          <IntegrationProviderIcon src={providerConfig.icon} />
          <Column spacing="2px">
            <Row variant="left" spacing={1.5}>
              <Typography variant="bodyPrimaryMed">
                {providerConfig.displayName}
              </Typography>
              {numConnected > 0 && (
                <TooltipWrapper
                  placement="bottom"
                  title={`${pluralizePhrase(
                    numConnected,
                    "Workspace member is",
                    { plural: "Workspace members are" }
                  )} using this integration`}
                >
                  <MemberCountChip
                    variant="left"
                    spacing="2px"
                    startIcon={
                      <PeopleIcon
                        sx={{ color: "shades.neutral.300", fontSize: "12px" }}
                      />
                    }
                  >
                    {numConnected}
                  </MemberCountChip>
                </TooltipWrapper>
              )}
            </Row>
            <Typography variant="labelReg" color="shades.neutral.500">
              {INTEGRATION_DETAIL_BY_PROVIDER_ID[setting.provider]}
            </Typography>
          </Column>
        </Row>
      </Row>
    </SettingRow>
  );
};

export default IntegrationCategoryRow;
